import { HTMLAttributes, ReactNode } from 'react';

/**
 * Badge — small inline status pill.
 *
 * Variants map to the Status color tokens:
 *   - `success` → status-green (Hunter)
 *   - `warning` → status-amber
 *   - `danger`  → status-red
 *   - `info`    → status-celadon
 *   - `neutral` → status-gray (default)
 *
 * Phase 11 Block 6 PR 2.
 */

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  dot?: boolean;
  children: ReactNode;
}

const variantStyles: Record<BadgeVariant, { bg: string; fg: string }> = {
  success: { bg: 'var(--color-status-green-bg)', fg: 'var(--color-status-green)' },
  warning: { bg: 'var(--color-status-amber-bg)', fg: 'var(--color-status-amber)' },
  danger: { bg: 'var(--color-status-red-bg)', fg: 'var(--color-status-red)' },
  info: { bg: 'var(--color-status-celadon-bg)', fg: 'var(--color-status-green)' },
  neutral: { bg: 'var(--color-status-gray-bg)', fg: 'var(--color-status-gray)' },
};

export function Badge({ variant = 'neutral', dot, children, style, ...rest }: BadgeProps) {
  const { bg, fg } = variantStyles[variant];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-1-5)',
        background: bg,
        color: fg,
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        padding: '2px 8px',
        borderRadius: 'var(--r-full)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wide)',
        ...style,
      }}
      {...rest}
    >
      {dot && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: fg,
          flexShrink: 0,
        }} />
      )}
      {children}
    </span>
  );
}
